import React, { useEffect, useReducer, useState } from 'react';
import { Box, Container, Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import GoogleFontsLoader from '../../../../components/Global/GoogleFontsLoader';
import Header from './Common/Header';
import Categories from './Common/Categories';
import MenuCard from './Common/MenuCard';
import Cart from './Common/Cart';
import { useParams } from 'react-router-dom';
import { useMutation as useRestMutation, useQuery as useRestQuery } from 'react-query';
import { capturePayment, createPayment, getMenuQuery } from '../Menu/queries';
import { createServedOrderAfterPayment, createServedOrderPayment, createTakeAwayOrderAfterPayment, createTakeAwayOrderPayment } from '../Menu/mutations';
import { initialState, reducer } from './Common/State';
import LoadingOverlay from './Common/LoadingOverlay';
import { calculateTotalPrice } from '../../../../utils/priceCalculator';
import InactivityModal from '../../../../components/Global/InactivityModal/index';
import { useInactivityModal } from '../../../../hooks/useInactivityModal';
import StartOrderCoverScreen from '../../../../components/Global/Location/Common/components/StartOrderCoverScreen';
import { useStartOrderCoverScreen } from '../../../../hooks/useStartOrderCoverScreen';
import { useOverscrollBackgroundColor } from '../../../../hooks/useOverscrollBackgroundColor';
import OrderTypeSelection from './Common/OrderTypeSelection';

const useStyles = makeStyles((theme: Theme) => ({
  layout: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    overflow: 'auto',
  },
  pageContainer: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  menuSection: {
    flex: 1,
    paddingRight: theme.spacing(3),
  },
  menuGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(2, 1fr)', // Two cards per row
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr', // One card per row for mobile
    },
  },
  categoryHeader: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: theme.spacing(2, 0),
    color: theme.palette.text.primary,
  },
  cartContainer: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    boxShadow: theme.shadows[1],
    position: 'sticky',
    top: theme.spacing(2),
    height: 'fit-content',
  },
  categoriesBar: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
    textAlign: 'center',
  },
}));

interface UrlParams {
  organisation_id: string;
  location_id: string;
}

interface MenuData {
  categories: Category[];
  location_info: any; // Replace with actual type
}

interface Category {
  id: string;
  name: string;
  category_type: 'DISH' | 'DRINK';
  dish_assignments: { dish: any }[]; // Replace with actual types
  drink_assignments: { drink: any }[];
}

const INACTIVITY_TIMEOUT = 30000;

const MainLayout: React.FC = () => {
  const { organisation_id, location_id } = useParams<UrlParams>();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize state from sessionStorage if available
  const getInitialState = () => {
    try {
      const sessionKey = `ordering_session_${location_id}`;
      const savedState = sessionStorage.getItem(sessionKey);
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return parsed;
      }
    } catch (error) {
      console.error('Failed to load session state:', error);
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(reducer, getInitialState());
  const { cart, orderDetails } = state;
  const [shouldShowStartOrderScreen, setShouldShowStartOrderScreen] = useState(false);

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    try {
      const sessionKey = `ordering_session_${location_id}`;
      sessionStorage.setItem(sessionKey, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save session state:', error);
    }
  }, [state, location_id]);

  const {
    data: menuData,
    isLoading,
    refetch,
  } = useRestQuery<MenuData>({
    queryKey: 'menu-data',
    queryFn: () => getMenuQuery(organisation_id, location_id),
  });

  useEffect(() => {
    if (menuData?.location_info?.google_font_url) {
      document.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]').forEach((link) => link.remove());

      const link = document.createElement('link');
      link.href = menuData.location_info.google_font_url;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    let fontStyleTag: HTMLStyleElement | null = null;
    if (menuData?.location_info?.google_font_family) {
      fontStyleTag = document.createElement('style');
      fontStyleTag.setAttribute('data-google-font-family', 'true');
      fontStyleTag.innerHTML = `body, body * { font-family: '${menuData.location_info.google_font_family}', sans-serif !important; }`;
      document.head.appendChild(fontStyleTag);
    }
    return () => {
      if (fontStyleTag) document.head.removeChild(fontStyleTag);
    };
  }, [menuData?.location_info?.google_font_url, menuData?.location_info?.google_font_family]);

  const color = menuData?.location_info?.ordering_system_primary_color || theme.palette.background.default;
  useOverscrollBackgroundColor(color);

  const { mutateAsync: createOrder, isLoading: creatingOrder } = useRestMutation({
    mutationKey: 'create-order',
    mutationFn: (request: string) => createPayment(request),
  });

  const { mutateAsync: captureOrder, isLoading: capturingOrder } = useRestMutation({
    mutationKey: 'capture-order',
    mutationFn: (request: any) => {
      return capturePayment(request.orderID, request.merchantId);
    },
  });

  const { mutateAsync: createServedOrderAfterPaymentAsync, isLoading: creatingOrderAfterPayment } = useRestMutation({
    mutationKey: 'create-order-after-payment',
    mutationFn: (order: any) => createServedOrderAfterPayment(order),
  });

  const { mutateAsync: createTakeAwayOrderAfterPaymentAsync, isLoading: creatingTakeAwayOrderAfterPayment } = useRestMutation({
    mutationKey: 'create-takeaway-order-after-payment',
    mutationFn: (order: any) => createTakeAwayOrderAfterPayment(order),
  });

  const { mutateAsync: createTakeAwayOrderPaymentAsync, isLoading: creatingTakeAwayOrderPayment } = useRestMutation({
    mutationKey: 'create-takeaway-order-payment',
    onSuccess: () => refetch(),
    mutationFn: (order: any) => createTakeAwayOrderPayment(order),
  });

  const { mutateAsync: createServedOrderPaymentAsync, isLoading: creatingServedOrderPayment } = useRestMutation({
    mutationKey: 'create-served-order-payment',
    onSuccess: () => refetch(),
    mutationFn: (order: any) => createServedOrderPayment(order),
  });

  const isPaymentProcessing =
    creatingTakeAwayOrderAfterPayment || creatingOrderAfterPayment || creatingTakeAwayOrderPayment || creatingServedOrderPayment || capturingOrder || creatingOrder;

  const [selectedCategory, setSelectedCategory] = React.useState<string>('');

  const sortCategories = (categories: Category[]) => {
    return categories.sort((a, b) => {
      if (a.category_type === 'DISH' && b.category_type === 'DRINK') return -1;
      if (a.category_type === 'DRINK' && b.category_type === 'DISH') return 1;
      //@ts-ignore
      return a.order_index - b.order_index;
    });
  };

  useEffect(() => {
    if (menuData?.categories?.[0]?.id) {
      const sortedCategories = sortCategories([...menuData.categories]);
      setSelectedCategory(sortedCategories[0].id);
    }
  }, [menuData]);

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  // Filter and sort menu items (dishes and drinks) by selected category
  const filteredMenuItems = React.useMemo(() => {
    const selectedCategoryData = menuData?.categories.find((category) => category.id === selectedCategory);

    if (!selectedCategoryData) return [];

    let items =
      selectedCategoryData.category_type === 'DISH'
        ? selectedCategoryData.dish_assignments.map((item) => item.dish)
        : selectedCategoryData.drink_assignments.map((item) => item.drink);

    return items.sort((a, b) => a.order_index - b.order_index);
  }, [menuData, selectedCategory]);
  //@ts-ignore
  const totalPrice = calculateTotalPrice(cart, menuData);

  const { inactivityOpen, inactivityModalKey, handleContinue, handleExit, handleModalClose } = useInactivityModal({
    isCartNotEmpty: Object.keys(cart).length > 0,
    onExit: () => window.location.reload(),
    inactivityTimeout: INACTIVITY_TIMEOUT,
  });

  const { isOpen: showStartScreen, close: handleStartOrder } = useStartOrderCoverScreen(cart, shouldShowStartOrderScreen);

  /**
   * Dispatched when the customer picks an order type in the OrderTypeSelection
   * modal.  Setting orderType on the shared reducer automatically hides the
   * modal (showOrderTypeSelection becomes false).
   */
  const handleOrderTypeSelect = (orderType: 'pickup' | 'delivery' | 'dinein') => {
    dispatch({
      type: 'UPDATE_ORDER_DETAILS',
      payload: { orderType },
    });
  };

  /**
   * Show the order-type selection modal when:
   *  - the StartOrderCoverScreen has been dismissed (!showStartScreen), AND
   *  - no order type has been chosen yet (from initial load or after a reset).
   * Skipped automatically when a previous session with a saved orderType is restored.
   */
  const showOrderTypeSelection = !showStartScreen && orderDetails.orderType === null;

  return (
    <>
      {menuData?.location_info && showStartScreen && <StartOrderCoverScreen onStart={handleStartOrder} locationInfo={menuData.location_info} location_id={location_id} />}
      {!showStartScreen && menuData?.location_info && (
        <div className={classes.layout}>
          <GoogleFontsLoader fontFamily={menuData?.location_info?.google_font_family} />
          <Header locationInfo={menuData.location_info} location_id={location_id} dispatch={dispatch} state={state} />
          <Box className={classes.categoriesBar}>
            {menuData?.categories && (
              <Categories categories={sortCategories([...menuData.categories])} selectedCategory={selectedCategory} onCategorySelect={handleCategorySelect} menuData={menuData} />
            )}
          </Box>
          <Container maxWidth={false} className={classes.pageContainer}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={8} className={classes.menuSection}>
                {selectedCategory && <Typography className={classes.categoryHeader}>{menuData?.categories.find((category) => category.id === selectedCategory)?.name}</Typography>}
                <div className={classes.menuGrid}>
                  {filteredMenuItems.map((item) => (
                    <MenuCard
                      key={item.id}
                      primary={menuData?.location_info?.ordering_system_primary_color}
                      secondary={menuData?.location_info?.ordering_system_secondary_color}
                      item={{
                        ...item,
                        imageUrl: item.image_added ? `${process.env.REACT_APP_AWS_IMAGES_URL}/${item.id}.jpeg` : '',
                      }}
                      cart={cart}
                      dispatch={dispatch}
                    />
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <div className={classes.cartContainer}>
                  <Cart
                    cart={cart}
                    dispatch={dispatch}
                    menuData={menuData}
                    totalPrice={totalPrice}
                    //@ts-ignore
                    orderDetails={orderDetails}
                    createOrder={createOrder}
                    captureOrder={captureOrder}
                    createServedOrderAfterPaymentAsync={createServedOrderAfterPaymentAsync}
                    createTakeAwayOrderAfterPaymentAsync={createTakeAwayOrderAfterPaymentAsync}
                    createTakeAwayOrderPaymentAsync={createTakeAwayOrderPaymentAsync}
                    createServedOrderPaymentAsync={createServedOrderPaymentAsync}
                    location_id={location_id}
                    organisation_id={organisation_id}
                    onOrderCompleted={() => setShouldShowStartOrderScreen(true)}
                  />
                </div>
              </Grid>
            </Grid>
          </Container>
          {isPaymentProcessing && <LoadingOverlay message="Processing your payment..." />}
          {/* Order type selection modal: shown after the cover screen dismissal, before menu interaction */}
          <OrderTypeSelection
            open={showOrderTypeSelection}
            onSelect={handleOrderTypeSelect}
            primaryColor={menuData.location_info.ordering_system_primary_color}
            secondaryColor={menuData.location_info.ordering_system_secondary_color}
          />
          <InactivityModal
            key={inactivityModalKey}
            open={inactivityOpen}
            onContinue={handleContinue}
            onExit={handleExit}
            onClose={handleModalClose}
            primaryColor={menuData?.location_info?.ordering_system_primary_color}
            secondaryColor={menuData?.location_info?.ordering_system_secondary_color}
            disableScrollLock
          />
        </div>
      )}
    </>
  );
};

export default MainLayout;
